// The component kit. Every visual decision here comes from the design system
// (DESIGN-SYSTEM.md): tokens only, no raw colors, fonts or radii.
import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, forwardRef, useEffect, useId, useRef, useState } from 'react'
import { Link, type LinkProps } from '@tanstack/react-router'
import { cx } from '../lib/cx'

// --- Button -----------------------------------------------------------------

type Variant = 'primary' | 'ink' | 'quiet' | 'danger'
const variants: Record<Variant, string> = {
  // One primary per screen: the single thing that matters right now.
  primary: 'bg-accent text-on-accent hover:brightness-105',
  ink: 'bg-ink text-paper',
  quiet: 'bg-transparent text-ink ring-[1.5px] ring-inset ring-line hover:ring-ink',
  danger: 'bg-transparent text-danger ring-[1.5px] ring-inset ring-current',
}
const buttonBase =
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold leading-tight transition active:scale-[.97] disabled:opacity-45 disabled:pointer-events-none select-none'
const sizes = { md: 'px-5 py-[11px] text-[15px]', sm: 'px-3.5 py-[7px] text-[13.5px]' }

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'md' | 'sm'; busy?: boolean; block?: boolean }

export function Button({ variant = 'quiet', size = 'md', busy, block, className, children, disabled, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cx(buttonBase, variants[variant], sizes[size], block && 'w-full', className)}
    >
      {busy && <Spinner />}
      {children}
    </button>
  )
}

export function ButtonLink({ variant = 'quiet', size = 'md', block, className, ...rest }: LinkProps & { variant?: Variant; size?: 'md' | 'sm'; block?: boolean; className?: string }) {
  return <Link {...rest} className={cx(buttonBase, variants[variant], sizes[size], block && 'w-full', className)} />
}

/** A plain anchor styled as a button, for what isn't a route: a file download. */
export function ButtonAnchor({ variant = 'quiet', size = 'md', block, className, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: Variant; size?: 'md' | 'sm'; block?: boolean }) {
  return <a {...rest} className={cx(buttonBase, variants[variant], sizes[size], block && 'w-full', className)} />
}

/** `track` draws the whole ring faintly under the arc. Use it where the
 *  spinner sits alone in the middle of something round (an avatar): an open
 *  ring's weight is off to one side, so it reads as off-centre and wobbles. */
export function Spinner({ className, track }: { className?: string; track?: boolean }) {
  return <span aria-hidden className={cx('inline-block size-4 animate-spin rounded-full border-2', track ? 'border-current/25 border-t-current' : 'border-current border-r-transparent', className)} />
}

// --- Field ------------------------------------------------------------------

type FieldProps = InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string; help?: string }

/** A labelled input. Native <input> with real name/autocomplete attributes,
 *  so password managers can fill and save. */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field({ label, error, help, id, className, ...rest }, ref) {
  const auto = useId()
  const fid = id ?? auto
  const note = error ?? help
  return (
    <div className="grid gap-1.5">
      <label htmlFor={fid} className="text-[13.5px] font-semibold">
        {label}
      </label>
      <input
        ref={ref}
        id={fid}
        aria-invalid={error ? true : undefined}
        aria-describedby={note ? `${fid}-note` : undefined}
        {...rest}
        className={cx(
          'w-full rounded-field border-[1.5px] bg-surface px-[13px] py-[11px] text-[16px] outline-none transition',
          'focus:border-accent-ink focus:ring-[3px] focus:ring-accent-wash',
          error ? 'border-danger' : 'border-line',
          className,
        )}
      />
      {note && (
        <p id={`${fid}-note`} className={cx('text-[13px]', error ? 'text-danger' : 'text-muted')}>
          {note}
        </p>
      )}
    </div>
  )
})

/** A labelled on/off choice. A real checkbox, so it's keyboard- and
 *  screen-reader-friendly, styled as a switch. */
export function Toggle({ label, labelHidden, help, checked, onChange, id }: { label: string; labelHidden?: boolean; help?: string; checked: boolean; onChange: (next: boolean) => void; id?: string }) {
  const auto = useId()
  const fid = id ?? auto
  return (
    <label htmlFor={fid} className={cx('flex cursor-pointer items-start justify-between', labelHidden ? 'justify-self-center' : 'gap-4')}>
      <span className={cx('grid gap-0.5', labelHidden && 'sr-only')}>
        <span className="text-[14.5px] font-semibold">{label}</span>
        {help && <span className="text-[13px] text-muted">{help}</span>}
      </span>
      <span className="relative mt-0.5 inline-flex flex-none">
        <input id={fid} type="checkbox" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="h-[28px] w-[48px] rounded-full bg-line transition peer-checked:bg-ink peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent" />
        <span className="absolute left-[3px] top-[3px] size-[22px] rounded-full bg-surface shadow transition peer-checked:translate-x-[20px]" />
      </span>
    </label>
  )
}

export function FormError({ children }: { children?: ReactNode }) {
  if (!children) return null
  return (
    <p role="alert" className="rounded-field bg-accent-wash px-3.5 py-2.5 text-[14px] text-ink">
      {children}
    </p>
  )
}

// --- Chips ------------------------------------------------------------------

type ChipTone = 'default' | 'selected' | 'live' | 'done'
const chipTones: Record<ChipTone, string> = {
  default: 'bg-sunken text-ink',
  selected: 'bg-ink text-paper',
  live: 'bg-accent-wash text-accent-ink',
  done: 'bg-transparent text-muted ring-[1.5px] ring-inset ring-line',
}

/** `size="tab"` is a chip you switch with (the category row): 15px text and
 *  at least 40px tall, so what a thumb aims at is a real target (sc-5184). */
export function Chip({ tone = 'default', size = 'regular', children, className }: { tone?: ChipTone; size?: 'regular' | 'tab'; children: ReactNode; className?: string }) {
  return (
    <span className={cx('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-semibold', size === 'tab' ? 'min-h-10 px-4 py-2.5 text-[15px]' : 'px-[11px] py-[5px] text-[13px]', chipTones[tone], className)}>
      {tone === 'live' && <span aria-hidden className="size-[7px] animate-pulse rounded-full bg-current" />}
      {children}
    </span>
  )
}

export type ReactionItem = { emoji: string; count: number; is_mine: boolean; by: string[] }

/** A post's reactions as chips (designs/20261017-reactions.md §4): each
 *  emoji with how many people put it there, mine in the selected tone, then
 *  the league's quick picks not yet on the post, and a "+" that opens the
 *  phone's own emoji keyboard through a one-character input. Under the row,
 *  who reacted with what. `readOnly` is your own post: counts and names,
 *  nothing to tap. */
export function ReactionRow({ reactions, quickPicks, readOnly, busy, onToggle, className }: {
  reactions: ReactionItem[]
  quickPicks: string[]
  readOnly?: boolean
  busy?: boolean
  onToggle: (emoji: string, on: boolean) => void
  className?: string
}) {
  const on = new Set(reactions.map((r) => r.emoji))
  const picks = quickPicks.filter((e) => !on.has(e))
  const chip = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-[11px] py-[5px] text-[13px] font-semibold transition-transform active:scale-90 disabled:active:scale-100'
  const named = reactions.filter((r) => r.by.length > 0)
  return (
    <div className={cx('grid gap-2', className)}>
      <div className="relative flex flex-wrap gap-2" role={readOnly ? undefined : 'group'} aria-label={readOnly ? undefined : 'React to this post'}>
        {reactions.map((r) => {
          const label = `${r.emoji}, ${r.count} ${r.count === 1 ? 'person' : 'people'}${r.is_mine ? ', you reacted' : ''}`
          if (readOnly)
            return (
              <Chip key={r.emoji} className="tabular-nums">
                <span role="img" aria-label={label}>{r.emoji}</span> {r.count}
              </Chip>
            )
          return (
            <button key={r.emoji} type="button" disabled={busy} aria-pressed={r.is_mine} aria-label={label} onClick={() => onToggle(r.emoji, !r.is_mine)}
              className={cx(chip, 'tabular-nums', r.is_mine ? chipTones.selected : chipTones.default)}>
              <span aria-hidden>{r.emoji}</span> {r.count}
            </button>
          )
        })}
        {!readOnly && picks.map((e) => (
          <button key={e} type="button" disabled={busy} aria-label={`React with ${e}`} onClick={() => onToggle(e, true)} className={cx(chip, chipTones.done)}>
            <span aria-hidden>{e}</span>
          </button>
        ))}
        {!readOnly && <EmojiPicker chip={cx(chip, chipTones.done, 'px-3')} busy={busy} exclude={on} onPick={(e) => onToggle(e, true)} />}
      </div>
      {named.length > 0 && (
        <p className="text-[12.5px] text-muted">
          {named.map((r, i) => (
            <span key={r.emoji}>
              {i > 0 && ' · '}
              <span aria-hidden>{r.emoji}</span> {listOf(r.by)}
            </span>
          ))}
        </p>
      )}
    </div>
  )
}

const moreEmoji = ['😂', '😍', '🥰', '😹', '😻', '😸', '😼', '🙀', '😮', '🤣', '😭', '🥹', '❤️', '🔥', '👏', '🙌', '💯', '✨', '🏆', '👑', '🐟', '🐾', '🧶', '📦', '😴', '🤔', '👀', '💀', '🫶', '🎉', '😎', '🤩']

/** The "+" of a ReactionRow: a card of emoji to tap, and a field for any
 *  other, typed from the phone's emoji keyboard or the Mac's emoji panel.
 *  (A hidden input that only took focus did nothing visible on a computer,
 *  and on a phone opened the letter keyboard.) Escape, a pick or a tap
 *  elsewhere closes it. */
function EmojiPicker({ chip, busy, exclude, onPick }: { chip: string; busy?: boolean; exclude: Set<string>; onPick: (emoji: string) => void }) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const box = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false)
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])
  const pick = (e: string) => { setOpen(false); setTyped(''); onPick(e) }
  return (
    <span ref={box} className="inline-flex">
      <button type="button" disabled={busy} aria-label="React with another emoji" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)} className={chip}>
        <span aria-hidden>+</span>
      </button>
      {open && (
        <div id={id} role="dialog" aria-label="Pick an emoji" className="absolute bottom-full right-0 z-30 mb-2 w-[272px] max-w-full rounded-card border border-line bg-surface p-2 shadow-lift">
          <div className="grid grid-cols-8 gap-0.5">
            {moreEmoji.filter((e) => !exclude.has(e)).map((e) => (
              <button key={e} type="button" aria-label={`React with ${e}`} onClick={() => pick(e)}
                className="flex aspect-square items-center justify-center rounded-full text-[20px] transition-transform hover:bg-sunken active:scale-90">
                <span aria-hidden>{e}</span>
              </button>
            ))}
          </div>
          <form className="mt-2" onSubmit={(ev) => { ev.preventDefault(); const v = typed.trim(); if (v) pick(v) }}>
            <input aria-label="Or type any emoji" placeholder="Or type any emoji" autoComplete="off" autoCapitalize="off" autoCorrect="off" enterKeyHint="send" maxLength={16} value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="w-full rounded-full border border-line bg-sunken px-3 py-1.5 text-[14px] text-ink placeholder:text-muted focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-accent" />
          </form>
        </div>
      )}
    </span>
  )
}

/** "Dana", "Dana and Marcus", "Dana, Marcus and you". */
function listOf(names: string[]) {
  if (names.length <= 1) return names.join('')
  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
}

// --- Bits -------------------------------------------------------------------

export function Label({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx('text-[11.5px] font-semibold uppercase tracking-[.12em] text-muted', className)}>{children}</span>
}

/** A person's or a cat's face. Always a circle, cropped to fill. With no
 *  photo (or one that fails to load) it's the placeholder for its kind, never
 *  initials. The placeholder paths are stand-ins for Rebecca's drawings and
 *  mirror #face-person / #face-cat in design/design-system.html.
 *  Sizes: default 26 (lists), `md` 40 (rows that lead with a face), `lg` 72. */
export function Avatar({ src, kind = 'person', size = 'sm', className }: { src?: string | null; kind?: 'person' | 'cat'; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const [broken, setBroken] = useState<string | null>(null)
  const dims = size === 'lg' ? 'size-[72px]' : size === 'md' ? 'size-10' : 'size-[26px]'
  return (
    <span aria-hidden className={cx('block flex-none overflow-hidden rounded-full bg-accent-wash text-accent-ink', dims, className)}>
      {src && broken !== src ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setBroken(src)} className="size-full object-cover" />
      ) : (
        <svg viewBox="0 0 40 40" className="size-full" fill="currentColor" opacity={0.62}>
          {kind === 'cat' ? (
            <path d="M6.5 40V24.5l3-16 8.3 6.9q2.2-.6 4.4 0l8.3-6.9 3 16V40z" />
          ) : (
            <>
              <circle cx="20" cy="15.5" r="7" />
              <path d="M5.5 40c0-9.5 6.2-14.5 14.5-14.5S34.5 30.5 34.5 40z" />
            </>
          )}
        </svg>
      )}
    </span>
  )
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="grid justify-items-center gap-2 rounded-card border-[1.5px] border-dashed border-line px-6 py-8 text-center">
      <h3 className="text-[17px] font-bold">{title}</h3>
      {children && <p className="max-w-[32ch] text-[14px] text-muted">{children}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  )
}

/** One row of the notification center (designs/20261020-notification-center.md
 *  §6): the actors' faces stacked, the server's sentence, how long ago, and a
 *  `nose` dot on the leading edge while it's unread. The whole card is the tap
 *  target; the screen reader hears the sentence, the time and "unread". */
export function NotificationRow({ faces, text, when, unread, onOpen }: { faces: (string | null | undefined)[]; text: string; when: string; unread?: boolean; onOpen?: () => void }) {
  return (
    <button type="button" onClick={onOpen} className="relative flex w-full items-center gap-3 rounded-card border border-line bg-surface py-3 pl-5 pr-4 text-left transition active:scale-[.99]">
      {unread && <i aria-hidden className="absolute left-2 top-1/2 size-[7px] -translate-y-1/2 rounded-full bg-nose" />}
      <span aria-hidden className="flex flex-none -space-x-3">
        {(faces.length ? faces : [null]).slice(0, 3).map((src, i) => (
          <Avatar key={i} src={src} size="md" className="ring-2 ring-surface" />
        ))}
      </span>
      <span className="grid min-w-0 gap-0.5">
        <span className={cx('text-[15px] leading-snug', unread && 'font-semibold')}>{text}</span>
        <span className="text-[13px] text-muted">
          {when}
          {unread && <span className="sr-only">, unread</span>}
        </span>
      </span>
    </button>
  )
}

/** A circled-i button that opens a small card of detail under it
 *  (designs/20261019-notification-settings.md §5): what a settings row
 *  covers, where a help line would crowd the row. Tap or Enter opens it,
 *  Escape, a second tap or a tap elsewhere closes it; the button is
 *  described by the card while it is open. */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false)
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])
  return (
    <span ref={box} className="relative inline-flex">
      <button type="button" aria-label={label} aria-expanded={open} aria-describedby={open ? id : undefined} onClick={() => setOpen((o) => !o)}
        className={cx('inline-flex size-[30px] items-center justify-center rounded-full text-muted transition hover:text-ink focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-accent', open && 'text-ink')}>
        <svg aria-hidden viewBox="0 0 20 20" className="size-[18px]" fill="none" stroke="currentColor" strokeWidth={1.7}>
          <circle cx="10" cy="10" r="8" />
          <path d="M10 9v5" strokeLinecap="round" />
          <circle cx="10" cy="6.2" r=".6" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <span id={id} role="tooltip" className="absolute left-0 top-full z-20 mt-1.5 w-[min(260px,70vw)] rounded-card border border-line bg-surface p-3 text-[13.5px] leading-snug text-ink shadow-lift">
          {children}
        </span>
      )}
    </span>
  )
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('rounded-card border border-line bg-surface p-4', className)}>{children}</div>
}

/** A label that leads straight into its number ("Submissions close in" /
 *  "2d 04:17"), with the supporting line below and quieter. The label and the
 *  detail must never read as one sentence, which is what happened when they
 *  shared a side and the number sat apart on the right. */
export function Banner({ label, detail, value, inverted = true }: { label: string; detail?: ReactNode; value: ReactNode; inverted?: boolean }) {
  return (
    <div className={cx('grid gap-0.5 rounded-card px-4 py-3', inverted ? 'bg-ink text-paper' : 'border border-line bg-surface text-ink')}>
      <span className="text-[11px] uppercase tracking-[.1em] opacity-70">{label}</span>
      <span className={cx('font-score text-[34px] font-bold leading-none tabular-nums tracking-[.02em]', inverted ? 'text-accent' : 'text-accent-ink')}>{value}</span>
      {detail && <span className="mt-1 text-[13px] opacity-[.72]">{detail}</span>}
    </div>
  )
}

export function PageTitle({ eyebrow, children, aside }: { eyebrow?: ReactNode; children: ReactNode; aside?: ReactNode }) {
  return (
    <header className="grid gap-1">
      <div className="flex items-center justify-between gap-2">
        {eyebrow ? <Label>{eyebrow}</Label> : <span />}
        {aside}
      </div>
      <h1 className="text-[28px] font-extrabold leading-[1.05] tracking-[-.025em]">{children}</h1>
    </header>
  )
}
